import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Calendar, ExternalLink, Leaf, Loader2, MapPin, Pencil, Star, Trash2, Wallet,
} from "lucide-react";

import { ClimateProjectFormDialog } from "@/components/ClimateProjectFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useClimateProject, useDeleteClimateProject,
} from "@/hooks/useClimateProjects";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

import { StatusPill } from "./ClimateProjects";
import { MetaRow, NotFound } from "./DocumentDetail";

/** /climate-projects/:id — full project detail. */
export default function ClimateProjectDetail() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id;

  const { data: user } = useCurrentUser();
  const detail = useClimateProject(id);
  const del    = useDeleteClimateProject();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);

  const canManage = !!user?.is_ceo_level;

  if (detail.isLoading) return <DetailSkeleton />;
  if (detail.error || !detail.data) return <NotFound back="/climate-projects" label="climate projects" />;

  const p = detail.data;

  async function onDelete() {
    const ok = await confirm({
      title: `Delete "${p.title}"?`,
      description: "The project will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete project",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(p.id, { onSuccess: () => navigate("/climate-projects") });
  }

  const money = p.finance_amount
    ? Number(p.finance_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Back + actions */}
      <div className="flex items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/climate-projects">
            <ArrowLeft className="mr-2 h-4 w-4" />Back to projects
          </Link>
        </Button>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-2 h-4 w-4" />Edit
            </Button>
            <Button variant="ghost" size="sm"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onDelete} disabled={del.isPending}
            >
              {del.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Hero */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <div className="relative aspect-[21/9] bg-gradient-to-br from-primary/15 to-emerald-700/10">
          {p.image_url ? (
            <img src={p.image_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Leaf className="h-16 w-16 text-primary/40" />
            </div>
          )}
          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            <StatusPill status={p.status} label={p.status_display} />
            <Badge variant="outline" className="bg-card/95 font-medium backdrop-blur">
              {p.project_type_display}
            </Badge>
            {p.is_featured && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30">
                <Star className="mr-1 h-3 w-3 fill-amber-400" />Featured
              </Badge>
            )}
            {!p.is_published && (
              <Badge variant="outline" className="bg-slate-900/70 text-white border-slate-900/70 backdrop-blur">
                Draft
              </Badge>
            )}
          </div>
        </div>

        <CardContent className="space-y-3 p-6 md:p-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {p.initiative}
          </div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{p.title}</h1>
          <p className="max-w-prose text-sm leading-relaxed text-foreground/80">{p.summary}</p>
          {p.project_url && (
            <Button asChild variant="outline" size="sm" className="mt-1">
              <a href={p.project_url} target="_blank" rel="noreferrer">
                Open project page
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Body + sidebar */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Finance card */}
        <Card className="border-border/60 shadow-soft lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-muted-foreground" />Finance
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 pt-0 sm:grid-cols-2">
            <FinanceTile
              label="Budget"
              value={money ? `${money} ${p.finance_currency}` : "—"}
            />
            <FinanceTile
              label="Funding source"
              value={p.funding_source || "—"}
            />
            <FinanceTile
              label="Timeline"
              value={[
                p.start_date ? formatDate(p.start_date) : null,
                p.end_date   ? formatDate(p.end_date)   : null,
              ].filter(Boolean).join(" → ") || "—"}
            />
            <FinanceTile
              label="Location"
              value={p.location || "Nationwide"}
              icon={<MapPin className="h-3 w-3" />}
            />
          </CardContent>
        </Card>

        {/* Details sidebar */}
        <Card className="h-fit border-border/60 shadow-soft">
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="space-y-3 pt-0 text-sm">
            <MetaRow label="Initiative"  value={p.initiative} />
            <MetaRow label="Type"        value={p.project_type_display} />
            <MetaRow label="Status"      value={p.status_display} />
            <MetaRow label="Owner"       value={p.owner_name || "—"} />
            <MetaRow
              label="Created"
              value={formatDateTime(p.created_at)}
              icon={<Calendar className="h-3 w-3" />}
            />
            <MetaRow label="Updated" value={formatDateTime(p.updated_at)} />
          </CardContent>
        </Card>
      </div>

      <ClimateProjectFormDialog open={editing} onOpenChange={setEditing} project={p} />
    </div>
  );
}

function FinanceTile({
  label, value, icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-md border bg-muted/20 p-3")}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}{label}
      </div>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-72 w-full" />
      <Skeleton className="h-44 w-full" />
    </div>
  );
}
